import { prettyErrorDescription } from 'constants/errors'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import formServices from 'services/form-builder-services'
import toast from 'react-hot-toast'
import { ColorDiv } from 'views/sign-up/styles/custom.style'
import OtpInput from 'components/otp-input'
import { dlTrackError, dlTrackEvent } from 'components/dataLayer/dataLayer'
import { useContext, useRef, useState } from 'react'
import { LoaderContext } from 'contexts/loader-context'
import ProfileService from 'services/profile-services'
import { useSession } from 'contexts/session-context'
import { AxiosError } from 'axios'
import ReCAPTCHA from 'react-google-recaptcha'

interface IProps {
  getValues: (name?: string) => { phoneNumber: string }
  onSubmit: (otp: string) => Promise<void>
}

const OtpPhoneV2 = ({ onSubmit, getValues }: IProps) => {
  const [resend, setResend] = useState(0)
  const [{ data: session }] = useSession()
  const { setLoad } = useContext(LoaderContext)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const { handleSubmit, setValue } = useForm({
    resolver: yupResolver(formServices.formGenerateSchema(["otp"])),
    criteriaMode: 'firstError',
    reValidateMode: 'onSubmit',
    mode: 'all'
  })

  const handleError = (error: AxiosError) => {
    if (error?.isAxiosError) {
      const { code: errorCode, message: errorMessage } = error?.response?.data ?? {}
      dlTrackError({
        error_type: 'login',
        error_code: { errorCode },
        error_message: { errorMessage },
        section: 'login'
      })
      toast.error(prettyErrorDescription(errorCode), { className: 'toast__error' })
    }
  }

  const handleConfirm = async (data) => {
    dlTrackEvent('form_send', {
      element: 'verificar',
      form_name: 'verifica tu numero de telefono',
      section: 'editar telefono'
    })
    await onSubmit(data?.otp)
  }

  const onInvalid = (errors: Record<string, any>) => {
    if (errors) {
      const errorsKeys = Object.values(errors)
      toast.error(errorsKeys[0]?.message, { className: 'toast__error' })
    }
  };

  const handleResend = async () => {
    if (resend > 2) return alert('No se puede reenviar el código, intenta más tarde.')

    recaptchaRef.current?.reset()
    await recaptchaRef.current?.executeAsync().then(async (recaptchaToken: any) => {
      setLoad(true)
      try {
        await ProfileService.otpGenerate({ phone: getValues('phoneNumber'), recaptchaToken, session })
        setResend(resend + 1)
      } catch (err) {
        handleError(err)
      } finally {
        setLoad(false)
      }
    })
  }

  return (
    <>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITEKEY} />
      <div className="pb-4">
        <h2 className="form__sign-in__title">Validar teléfono</h2>
        <p className="text big text-center mb-8">Te enviamos un código de 6 dígitos al <b>{getValues('phoneNumber')}</b> para que lo ingreses y valides tu numero celular. Ingresalo a continuación para poder verificarlo.</p>
        <form onSubmit={handleSubmit(handleConfirm, onInvalid)} className='space-y-4'>
          <ColorDiv className="gap-2 flex flex-col mb-3 input-container">
            <OtpInput setValue={setValue} />
          </ColorDiv>
          <div className='space-y-4 flex flex-col items-center'>
            <button type="submit" className="form__sign-in__btn form__sign-in__btn__main">
              Validar
            </button>
            {resend < 2 && <button type='button' className="text small text-center underline uppercase cursor-pointer" onClick={handleResend}>REENVIAR CÓDIGO</button>}
          </div>
        </form>
      </div>
    </>
  )
}

export default OtpPhoneV2
